import { CdkDragDrop, transferArrayItem } from '@angular/cdk/drag-drop'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'

import { Subscription } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { Tag } from 'src/app/modules/commons/models/tag.model'
import {
  ComputedRecord,
  MachineStateComputed,
} from 'src/app/modules/touareg-control/models/states-computed.models'
import { TouaregControlDisplayService } from 'src/app/modules/touareg-control/services/touareg-control-display.service'
import { TouaregControlStateMachineService } from 'src/app/modules/touareg-control/services/touareg-control-state-machine.service'
import {
  StateMachineSettings,
  TouaregControlStateParamsService,
} from 'src/app/modules/touareg-control/services/touareg-control-state-params.service'
import { State } from '../../../models/states.models'
import { OverlayComponent } from '@/modules/commons/components/overlay/overlay.component';
import { TouaregControlStateRowComponent } from '../components/touareg-control-state-row/touareg-control-state-row.component';
import { TouaregControlSearchComponent } from '../../touareg-control-search/touareg-control-search.component';
import { Samv2Module } from '@aubert-et-duval/samv2';
import { TouaregControlStateAssetsComponent } from '../components/touareg-control-state-assets/touareg-control-state-assets.component';
import { TouaregControlStateInfoComponent } from '../components/touareg-control-state-info/touareg-control-state-info.component';
import { TouaregControlProgressCountdownComponent } from '../components/touareg-control-progress-countdown/touareg-control-progress-countdown.component';

export interface TouaregControlLayoutMessage {
  body?: string
  list?: string[]
  type?: 'error' | 'warning' | 'info'
  title?: string
  icon?: string
  matIcon?: string
}

@Component({
  selector: 'app-touareg-control-state-layout',
  templateUrl: './touareg-control-state-layout.component.html',
  styleUrls: ['./touareg-control-state-layout.component.scss'],
  imports: [OverlayComponent, TouaregControlStateRowComponent, TouaregControlSearchComponent, Samv2Module, TouaregControlStateAssetsComponent, TouaregControlStateInfoComponent, TouaregControlProgressCountdownComponent],
})
export class TouaregControlStateLayoutComponent implements OnInit, OnDestroy {
  @Output()
  onSuggestion = new EventEmitter<ComputedRecord[]>()

  @Output()
  onSelection = new EventEmitter<ComputedRecord[]>()

  @Output()
  deSelection = new EventEmitter<ComputedRecord[]>()

  @Output()
  onManualAdd = new EventEmitter<Tag[]>()

  @Input()
  error: any

  @Input()
  message: TouaregControlLayoutMessage | null = null

  @Input()
  noShow!: boolean

  @Input()
  noShowMessage!: string

  @Input()
  loading!: boolean

  @Input()
  waiting!: boolean

  @Input()
  locked!: boolean

  @Input()
  centralButton!: boolean

  @Input()
  showActions!: boolean

  @Input()
  showSearch!: boolean

  @Input()
  showCountdown!: boolean

  @Input()
  countdownMessage!: string

  // @Input()
  // suggestionList: any[]

  // @Input()
  // selectionList: any[]

  private _touaregStatesSubs$!: Subscription
  private _touaregDisplayParamsSubs$!: Subscription
  private _touaregDisplaySubs$!: Subscription

  public currentState: MachineStateComputed | null | undefined
  public suggestionLocalList: ComputedRecord[] = []
  public selectionLocalList: ComputedRecord[] = []
  public DRAG_START_DELAY: number = 150
  public settings!: StateMachineSettings
  public opens: any = {}

  constructor(
    private _stateMachine: TouaregControlStateMachineService,
    private _params: TouaregControlStateParamsService,
    private _display: TouaregControlDisplayService,
    private _translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Listen settings params
    this._touaregDisplayParamsSubs$ = this._params.settings$.subscribe(
      (settings) => (this.settings = settings)
    )

    // Listen display service
    this._touaregDisplaySubs$ = this._display.pairingsInfoOpen$.subscribe(
      (opens) => {
        this.opens = opens
      }
    )

    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this._touaregStatesSubs$ = this._stateMachine.stateMachine$.subscribe(
      (state) => {
        this.currentState = state

        if (state) {
          if (state.state === State.IDLE) {
            this._display.resetPairingInfoOpen()
          }
          const { suggestion, selection } = state.computed
          this.suggestionLocalList = suggestion
          this.selectionLocalList = selection

          // Gestion des messages
          let list: string[] = []
          if (state.computed.warningList?.length) {
            list = [...state.computed.warningList]
          }
          if (state.computed.pairingWarningList?.length) {
            list = [...state.computed.pairingWarningList]
          }
          if (list.length) {
            this.message = {
              type: 'warning',
              matIcon: 'warning',
              title: this._translate.instant('touaregController.warningChoices'),
              list,
            }
          } else {
            this.message = null
          }
        } else {
          this.suggestionLocalList = []
          this.selectionLocalList = []
          this.message = null
        }
      }
    )
  }

  public selectTag(tag: ComputedRecord) {
    this.onSelection.emit([tag])
  }

  public rejectTag(tag: ComputedRecord) {
    this.onSuggestion.emit([tag])
  }

  public onSelectAll(e: MouseEvent) {
    this.onSelection.emit(this.suggestionLocalList)
  }

  public deSelectAll(e: MouseEvent) {
    this.deSelection.emit(this.selectionLocalList)
  }

  public onCountdownComplete() {}

  public onOpenTagInfo(id: string | number) {
    this._display.setPairingInfoOpen(id)
  }

  public onCloseTagInfo(id: string | number) {
    this._display.setPairingInfoClose(id)
  }

  public onAdd(tags: Tag[]) {
    this.onManualAdd.emit(tags)
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    if (!!this._touaregStatesSubs$) this._touaregStatesSubs$.unsubscribe()
    if (!!this._touaregDisplaySubs$) this._touaregDisplaySubs$.unsubscribe()
    if (!!this._touaregDisplayParamsSubs$)
      this._touaregDisplayParamsSubs$.unsubscribe()
  }
}
